import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Activity, TrendingDown, TrendingUp } from 'lucide-react';
import { brandDependencyData, scenarioDefinitions, scenarioOptions } from './dependencyData';

// Risk Level Badge
const RiskBadge = ({ level }) => {
  const config = {
    low: { bg: 'bg-stable/20', text: 'text-stable', border: 'border-stable/30', label: 'Low Risk' },
    medium: { bg: 'bg-warning/20', text: 'text-warning', border: 'border-warning/30', label: 'Medium Risk' },
    high: { bg: 'bg-destructive/20', text: 'text-destructive', border: 'border-destructive/30', label: 'High Risk' },
  };
  const c = config[level] || config.medium;
  return (
    <Badge className={`${c.bg} ${c.text} ${c.border} border font-semibold`}>
      {c.label}
    </Badge>
  );
};

// Comparison Bar Component
const ComparisonBar = ({ label, current, simulated, max, unit = '', isNegativeGood = false }) => {
  const diff = simulated - current;
  const isPositive = diff >= 0;
  const displayDiff = isPositive ? `+${diff}` : diff;
  
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {current}{unit} → {simulated}{unit}
          </span>
          <span className={`text-xs font-bold ${
            (isPositive && !isNegativeGood) || (!isPositive && isNegativeGood) 
              ? 'text-stable' 
              : 'text-destructive'
          }`}>
            ({displayDiff}{unit})
          </span>
        </div>
      </div>
      <div className="relative h-6 bg-muted/30 rounded-full overflow-hidden">
        {/* Current value bar */}
        <div 
          className="absolute top-0 left-0 h-full bg-primary/40 rounded-full"
          style={{ width: `${(current / max) * 100}%` }}
        />
        {/* Simulated value bar */}
        <div 
          className={`absolute top-0 left-0 h-full rounded-full ${
            simulated >= current ? 'bg-stable/60' : 'bg-destructive/60'
          }`}
          style={{ width: `${(simulated / max) * 100}%` }}
        />
        {/* Labels */}
        <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-medium">
          <span className="text-foreground">Current: {current}{unit}</span>
          <span className={simulated >= current ? 'text-stable' : 'text-destructive'}>
            Simulated: {simulated}{unit}
          </span>
        </div>
      </div>
    </div>
  );
};

export const RiskSimulation = ({ selectedScenario, onScenarioChange }) => {
  const { teamBaseline } = brandDependencyData;
  const scenario = scenarioDefinitions[selectedScenario];
  
  // Calculate simulated values
  const simulatedScore = teamBaseline.brandScore + scenario.brandScoreAdjustment;
  const simulatedValueLow = teamBaseline.brandValueLow + scenario.valueLowAdjustment;
  const simulatedValueHigh = teamBaseline.brandValueHigh + scenario.valueHighAdjustment;
  const simulatedRank = teamBaseline.concacafRank + scenario.rankShift;

  return (
    <section className="mb-10" data-testid="risk-simulation-section">
      <div className="flex items-center gap-3 mb-6">
        <Activity className="w-6 h-6 text-destructive" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Risk Simulation</h2>
      </div>
      
      <Card className="bg-card border-border/50">
        <CardContent className="p-6">
          {/* Scenario Selector */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-3">
                Select Scenario
              </label>
              <Select value={selectedScenario} onValueChange={onScenarioChange}>
                <SelectTrigger 
                  className="w-full bg-muted/30 border-border/50"
                  data-testid="scenario-dropdown"
                >
                  <SelectValue placeholder="Select scenario" />
                </SelectTrigger>
                <SelectContent>
                  {scenarioOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex flex-col">
                        <span>{option.label}</span>
                        <span className="text-xs text-muted-foreground">{option.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Scenario Summary */}
            <div className="p-4 rounded-lg bg-muted/20 border border-border/30">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-foreground">{scenario.label}</span>
                <RiskBadge level={scenario.riskLevel} />
              </div>
              <p className="text-sm text-muted-foreground">{scenario.description}</p>
            </div>
          </div>
          
          {/* Comparison Bars */}
          <div className="space-y-2">
            <ComparisonBar 
              label="Team Brand Score"
              current={teamBaseline.brandScore}
              simulated={simulatedScore}
              max={100}
            />
            
            <ComparisonBar 
              label="Brand Value (Low)"
              current={teamBaseline.brandValueLow}
              simulated={simulatedValueLow}
              max={400}
              unit="M"
            />
            
            <ComparisonBar 
              label="Brand Value (High)"
              current={teamBaseline.brandValueHigh}
              simulated={simulatedValueHigh}
              max={400}
              unit="M"
            />
          </div>
          
          {/* Results Summary */}
          <div className="grid md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border/50">
            <div className="p-4 rounded-lg bg-muted/20">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Adjusted Score</p>
              <p className={`text-2xl font-bold ${simulatedScore < teamBaseline.brandScore ? 'text-destructive' : 'text-stable'}`}>
                {simulatedScore}
              </p>
            </div>
            
            <div className="p-4 rounded-lg bg-muted/20">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Value Range</p>
              <p className={`text-lg font-bold ${simulatedValueLow < teamBaseline.brandValueLow ? 'text-destructive' : 'text-foreground'}`}>
                ${simulatedValueLow}M - ${simulatedValueHigh}M
              </p>
            </div>
            
            <div className="p-4 rounded-lg bg-muted/20">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">CONCACAF Rank</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-foreground">#{simulatedRank}</p>
                {scenario.rankShift > 0 && (
                  <div className="flex items-center text-destructive text-xs">
                    <TrendingDown className="w-3 h-3" />
                    <span>-{scenario.rankShift}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-4 rounded-lg bg-muted/20">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Risk Level</p>
              <RiskBadge level={scenario.riskLevel} />
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default RiskSimulation;
